import { useCallback } from 'react';
import get from 'lodash/get';
import { useHistory, useParams } from 'react-router-dom';
import routePaths, { routePatterns } from 'router/route-paths';
import { messageOf } from 'utils/error';

export interface IAuthFormValues {
  firstName?: string;
  lastName?: string;
}

interface IAuthProfile {
  projectId?: string;
  firstName?: string;
  lastName?: string;
}

/** The variables of the auth mutation: the form values with the name moved into `profile`. */
export type TAuthArgs<TValues extends IAuthFormValues> = Omit<TValues, 'firstName' | 'lastName'> & {
  profile: IAuthProfile;
  projectId?: string;
};

/**
 * The formik helpers the submit uses; `form` holds the form-level error, `otpInvalid` the error of the
 * admin login OTP (set by the login page's `onError`).
 */
export interface IAuthFormActions {
  setSubmitting: (isSubmitting: boolean) => void;
  setErrors: (errors: { form?: string; otpInvalid?: string }) => void;
}

interface IAuthFormSubmitOptions {
  onSuccess?: () => void;
}

const useAuthFormSubmit = <TValues extends IAuthFormValues>(
  auth: (args: TAuthArgs<TValues>) => Promise<unknown>,
  { onSuccess }: IAuthFormSubmitOptions = {},
) => {
  const history = useHistory();
  const params = useParams<{ id?: string }>();
  const { id } = params;

  return useCallback(
    async (
      { firstName, lastName, ...rest }: TValues,
      formikActions: IAuthFormActions,
      onError?: (
        error: unknown,
        authFunction: (args: TAuthArgs<TValues>) => Promise<unknown>,
        args: TAuthArgs<TValues>,
        formikActions: IAuthFormActions,
      ) => void,
    ) => {
      const { setSubmitting, setErrors } = formikActions;
      setSubmitting(true);
      const args = { profile: { projectId: id, firstName, lastName }, projectId: id, ...rest };
      try {
        const data = await auth(args);
        const emailVerified: unknown = get(data, 'user.emails[0].verified');
        if (emailVerified) {
          if (id) {
            history.push(routePatterns.dashboard.stringify({ id }));
          } else {
            history.push(routePaths.myProjects);
          }
        }
        if (typeof onSuccess === 'function') onSuccess();
        setSubmitting(false);
      } catch (error) {
        if (typeof onError === 'function') onError(error, auth, args, formikActions);
        if (messageOf(error) === 'AUTH_OTP_REQUIRED_ERROR') return;
        setErrors({ form: messageOf(error) });
        setSubmitting(false);
      } finally {
        setSubmitting(false);
      }
    },
    [auth, history, id, onSuccess],
  );
};

export default useAuthFormSubmit;
